<script setup lang="ts">
/**
 * PersonalFittings — Left-panel filters + fit list for personal EVE fittings
 * Uses dropdowns for class/hull filtering (populated from actual user data).
 * Includes sort by date and search.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useAuth } from '../../composables/useAuth';
import { useFitStore } from '../../stores/fitStore';
import {
  browsePersonalFits, syncPersonalFittings,
  fetchPersonalShipClasses, fetchPersonalHulls,
  importEveFit, iconUrl,
} from '../../composables/useApi';
import type { PersonalFit, PersonalFitsResponse, ShipClass, Ship } from '../../../shared/types';

const emit = defineEmits<{ 'select-fit': [fitID: number] }>();

const { isLoggedIn, characterName, characterId, login } = useAuth();
const store = useFitStore();

const loading = ref(false);
const syncing = ref(false);
const shipClasses = ref<ShipClass[]>([]);
const selectedClassID = ref<number | null>(null);
const hulls = ref<Ship[]>([]);
const selectedHullTypeID = ref<number | null>(null);
const searchQuery = ref('');
const sortOrder = ref<'desc' | 'asc'>('desc');
const page = ref(1);
const response = ref<PersonalFitsResponse | null>(null);
const hasSynced = ref(false);

const fits = computed(() => response.value?.fits ?? []);
const totalPages = computed(() => response.value?.totalPages ?? 0);
const totalFits = computed(() => response.value?.total ?? 0);

watch(isLoggedIn, (v) => { if (v && !hasSynced.value) loadData(); });
onMounted(() => { if (isLoggedIn.value) loadData(); });

async function loadData(): Promise<void> {
  try { shipClasses.value = await fetchPersonalShipClasses(); } catch { shipClasses.value = []; }
  await loadFits();
}

async function loadFits(): Promise<void> {
  if (!isLoggedIn.value) return;
  loading.value = true;
  try {
    response.value = await browsePersonalFits(
      page.value, selectedClassID.value ?? undefined,
      selectedHullTypeID.value ?? undefined, searchQuery.value || undefined,
    );
  } catch { response.value = null; }
  finally { loading.value = false; }
}

async function handleSync(): Promise<void> {
  syncing.value = true;
  try {
    await syncPersonalFittings();
    hasSynced.value = true;
    store.addToast('success', 'Fittings synced from EVE');
    try { shipClasses.value = await fetchPersonalShipClasses(); } catch {}
    page.value = 1;
    await loadFits();
  } catch {} finally { syncing.value = false; }
}

async function onClassChange(e: Event): Promise<void> {
  const val = (e.target as HTMLSelectElement).value;
  selectedClassID.value = val ? parseInt(val, 10) : null;
  selectedHullTypeID.value = null;
  hulls.value = [];
  page.value = 1;
  if (selectedClassID.value) {
    try { hulls.value = await fetchPersonalHulls(selectedClassID.value); } catch { hulls.value = []; }
  }
  await loadFits();
}

async function onHullChange(e: Event): Promise<void> {
  const val = (e.target as HTMLSelectElement).value;
  selectedHullTypeID.value = val ? parseInt(val, 10) : null;
  page.value = 1;
  await loadFits();
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null;
function handleSearchInput(): void {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => { page.value = 1; loadFits(); }, 350);
}

async function handleImportFit(fit: PersonalFit): Promise<void> {
  try {
    const fittingData = JSON.parse(fit.fitting_data);
    const result = await importEveFit(fittingData);
    store.addToast('success', `Imported: ${fit.fitting_name}`);
    emit('select-fit', result.fitID);
  } catch {}
}

async function goToPage(p: number): Promise<void> {
  if (p < 1 || p > totalPages.value) return;
  page.value = p;
  await loadFits();
}
</script>

<template>
  <div class="personal-panel">
    <!-- Not logged in -->
    <div v-if="!isLoggedIn" class="login-prompt">
      <svg width="36" height="36" viewBox="0 0 32 32" class="stub-icon">
        <circle cx="16" cy="12" r="5" fill="none" stroke="currentColor" stroke-width="1.5" />
        <path d="M8 26c0-5 4-8 8-8s8 3 8 8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      <span class="stub-title">Personal Fittings</span>
      <span class="stub-sub">Login with EVE to view and import your in-game fittings</span>
      <button class="login-btn" @click="login">Login with EVE Online</button>
    </div>

    <!-- Logged in -->
    <template v-else>
      <!-- User header -->
      <div class="user-header">
        <img v-if="characterId" :src="`https://images.evetech.net/characters/${characterId}/portrait?size=64`" class="char-portrait" :alt="characterName ?? ''" />
        <div class="user-info">
          <span class="user-name">{{ characterName }}</span>
          <button class="sync-btn" :disabled="syncing" @click="handleSync">
            {{ syncing ? 'Syncing...' : 'Sync from EVE' }}
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-section">
        <div class="filter-group">
          <label class="filter-label">Search</label>
          <input v-model="searchQuery" type="text" class="filter-input" placeholder="Search..." @input="handleSearchInput" />
        </div>

        <div class="filter-group">
          <label class="filter-label">Ship Class</label>
          <select class="filter-select" :value="selectedClassID ?? ''" @change="onClassChange">
            <option value="">All Classes</option>
            <option v-for="sc in shipClasses" :key="sc.groupID" :value="sc.groupID">{{ sc.groupName }}</option>
          </select>
        </div>

        <div v-if="hulls.length > 0" class="filter-group">
          <label class="filter-label">Hull</label>
          <select class="filter-select" :value="selectedHullTypeID ?? ''" @change="onHullChange">
            <option value="">All Hulls</option>
            <option v-for="hull in hulls" :key="hull.typeID" :value="hull.typeID">{{ hull.typeName }}</option>
          </select>
        </div>

        <div class="filter-group">
          <label class="filter-label">Sort</label>
          <select class="filter-select" v-model="sortOrder" @change="loadFits">
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      <!-- Count -->
      <div class="result-count" v-if="!loading && response">
        {{ totalFits }} fitting{{ totalFits !== 1 ? 's' : '' }}
      </div>

      <!-- Fit list -->
      <div class="fit-list">
        <div v-if="loading" class="list-status">Loading...</div>
        <div v-else-if="fits.length === 0" class="list-status">
          <template v-if="!hasSynced && totalFits === 0">Click "Sync from EVE" to import your in-game fittings</template>
          <template v-else>No fittings found</template>
        </div>

        <div v-for="fit in fits" :key="fit.id" class="fit-card">
          <img :src="iconUrl(fit.ship_type_id, 32)" :alt="fit.ship_name" class="fit-icon" loading="lazy" />
          <div class="fit-info">
            <span class="fit-name">{{ fit.fitting_name }}</span>
            <span class="fit-meta">{{ fit.ship_name }}</span>
          </div>
          <button class="import-btn" @click="handleImportFit(fit)">Import</button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">&laquo;</button>
        <span class="page-info">{{ page }} / {{ totalPages }}</span>
        <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">&raquo;</button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.personal-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Login prompt */
.login-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 10px;
  padding: 30px 20px;
  color: var(--text-secondary);
  text-align: center;
}

.stub-icon { opacity: 0.3; }

.stub-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);
}

.stub-sub { font-size: 0.85rem; }

.login-btn {
  margin-top: 8px;
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius-md);
  background: var(--accent-blue);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.login-btn:hover { background: #3a8eef; }

/* User header */
.user-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-darker);
  flex-shrink: 0;
}

.char-portrait {
  width: 36px;
  height: 36px;
  border-radius: 4px;
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
}

.sync-btn {
  align-self: flex-start;
  padding: 4px 12px;
  border: 1px solid var(--accent-blue);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--accent-blue);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sync-btn:hover:not(:disabled) { background: var(--accent-blue); color: #fff; }
.sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Filters */
.filter-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 0.72rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 500;
}

.filter-input,
.filter-select {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-darker);
  color: var(--text-primary);
  font-size: 0.85rem;
  font-family: inherit;
  transition: border-color var(--transition-fast);
}

.filter-select { cursor: pointer; appearance: auto; }

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.result-count {
  font-size: 0.8rem;
  color: var(--accent-blue);
  font-weight: 500;
  padding: 8px 14px;
  flex-shrink: 0;
}

/* Fit list */
.fit-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.list-status {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
  padding: 30px 14px;
}

.fit-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background var(--transition-fast);
}

.fit-card:hover { background: var(--bg-hover); }

.fit-icon {
  width: 32px;
  height: 32px;
  border-radius: 3px;
  flex-shrink: 0;
}

.fit-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.fit-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fit-meta {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.import-btn {
  padding: 5px 12px;
  border: 1px solid var(--accent-blue);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--accent-blue);
  font-size: 0.78rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.import-btn:hover { background: var(--accent-blue); color: #fff; }

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
}

.page-btn {
  padding: 5px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
}

.page-btn:hover:not(:disabled) { color: var(--text-primary); border-color: var(--accent-blue); }
.page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.page-info { font-size: 0.8rem; color: var(--text-secondary); }
</style>
